import { API_BASE } from "./api";

export const ORG_LOGO_STORAGE_KEY = "naturals_partner_org_logo";
export const ORG_LOGO_UPDATED_EVENT = "naturals-org-logo-updated";

/** Same default as Page Setup / org brand when portal/company logo is missing. */
export const ENTERPRISE_DEFAULT_ORG_LOGO =
  "https://beupun58livmdh3c.public.blob.vercel-storage.com/f5bc5113250ac95d_logo-zla24T1ZZYQQDiyGCGUWigu24lx2FD.png";

const STORAGE_KEY_PREFIXES = ["portal-logos/", "public-assets/"];

function isAbsoluteMediaUrl(text) {
  return (
    text.startsWith("http://") ||
    text.startsWith("https://") ||
    text.startsWith("data:") ||
    text.startsWith("blob:")
  );
}

function apiBase() {
  return String(API_BASE || "").replace(/\/$/, "");
}

function isStorageObjectKey(path) {
  const clean = String(path || "").replace(/^\//, "").split("?")[0];
  return STORAGE_KEY_PREFIXES.some((prefix) => clean.startsWith(prefix));
}

/** True when a URL incorrectly points at the admin API host for a storage object key. */
function isApiHostedObjectUrl(text) {
  try {
    const base = apiBase();
    if (!base || !text.startsWith(base)) return false;
    const rest = text.slice(base.length).replace(/^\//, "").split("?")[0];
    if (!rest) return false;
    // Known storage prefixes are served by GET /portal-logos/* and /public-assets/*.
    if (isStorageObjectKey(rest)) return false;
    // Bare filenames must not be loaded via the admin API (405).
    if (rest.includes("/")) return false;
    return /\.(png|jpe?g|webp|gif|svg|ico)$/i.test(rest);
  } catch (_) {
    return false;
  }
}

function toApiMediaUrl(path) {
  const base = apiBase();
  if (!base) return null;
  const clean = String(path || "").replace(/^\//, "").split("?")[0];
  if (!isStorageObjectKey(clean)) return null;
  return `${base}/${clean}`;
}

function isE2eObjectStoreUrl(text) {
  return /objectstore\.e2enetworks\.net/i.test(text);
}

export function normalizeMediaStorageRef(value) {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (text.startsWith("data:") || text.startsWith("blob:")) return text;
  for (const prefix of STORAGE_KEY_PREFIXES) {
    const idx = text.indexOf(prefix);
    if (idx >= 0) {
      return text.slice(idx).split("?")[0];
    }
  }
  if (!text.startsWith("http://") && !text.startsWith("https://")) {
    return text.replace(/^\//, "").split("?")[0];
  }
  return text;
}

/**
 * Resolve organization / media logo URLs for display.
 * Absolute http(s)/data URLs are returned as-is (unless they wrongly hit the admin API).
 * Relative portal-logos / public-assets keys are served via the salon-admin GET media routes.
 */
export function resolveMediaUrl(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;

  if (isAbsoluteMediaUrl(text)) {
    if (isApiHostedObjectUrl(text)) return null;
    if (isE2eObjectStoreUrl(text)) {
      const key = normalizeMediaStorageRef(text);
      const apiUrl = toApiMediaUrl(key);
      if (apiUrl) return apiUrl;
    }
    // Already pointing at admin API media routes — keep (GET handlers serve them).
    try {
      const base = apiBase();
      if (base && text.startsWith(base)) {
        const rest = text.slice(base.length).replace(/^\//, "").split("?")[0];
        if (isStorageObjectKey(rest)) return text.split("?")[0];
      }
    } catch (_) {
      /* ignore */
    }
    return text;
  }

  if (text.startsWith("/uploads/") || text.startsWith("uploads/")) {
    const path = text.replace(/^\//, "");
    return `${apiBase()}/${path}`;
  }

  const storageUrl = toApiMediaUrl(text);
  if (storageUrl) return storageUrl;

  // Bare object keys / filenames are not loadable via the admin API (405/404).
  return null;
}

export function persistOrgLogo(url) {
  try {
    const resolved = resolveMediaUrl(url);
    if (resolved) {
      localStorage.setItem(ORG_LOGO_STORAGE_KEY, resolved);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(ORG_LOGO_UPDATED_EVENT, { detail: { url: resolved } })
        );
      }
      return resolved;
    }
    localStorage.removeItem(ORG_LOGO_STORAGE_KEY);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(ORG_LOGO_UPDATED_EVENT, { detail: { url: null } }));
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

export function resolveStoredOrgLogo() {
  try {
    const raw = localStorage.getItem(ORG_LOGO_STORAGE_KEY);
    const resolved = resolveMediaUrl(raw);
    if (raw && !resolved) {
      localStorage.removeItem(ORG_LOGO_STORAGE_KEY);
    }
    return resolved;
  } catch (_) {
    return null;
  }
}

/** Partner sidebar fallback: org logo → stored → Naturals brand (never Nyafaz product mark). */
export function resolvePartnerLogoSrc(orgLogo) {
  return (
    resolveMediaUrl(orgLogo) ||
    resolveStoredOrgLogo() ||
    ENTERPRISE_DEFAULT_ORG_LOGO
  );
}
